import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Mail, Copy, Check, Calendar, Target, Trophy, Quote, ShieldCheck } from 'lucide-react';
import { ExecAvatar, EXEC_ACCENTS, TAG_HEX } from './ExecutiveCard';

const Stat = ({ Icon, label, value, hex }) => (
  <div className="p-3 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
    <Icon className="w-3.5 h-3.5 mb-1.5" style={{ color: hex }} />
    <p className="text-xl font-light text-white tabular-nums leading-none">{value}</p>
    <p className="text-[9px] tracking-[0.16em] uppercase text-white/40 mt-1.5">{label}</p>
  </div>
);

const Label = ({ Icon, children }) => <p className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-white/35 font-semibold mb-3">{Icon && <Icon className="w-3 h-3" />}{children}</p>;

// Centred profile modal with keyboard + arrow navigation between executives
export const ExecutiveProfileDrawer = ({ execs, index, onClose, onNavigate }) => {
  const [copied, setCopied] = useState(false);
  const m = execs[index];
  const hex = EXEC_ACCENTS[index % EXEC_ACCENTS.length];
  const go = (d) => onNavigate((index + d + execs.length) % execs.length);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowRight') go(1); if (e.key === 'ArrowLeft') go(-1); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  });
  useEffect(() => setCopied(false), [index]);

  const copy = () => { navigator.clipboard?.writeText(m.contact.email); setCopied(true); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="executive-profile-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} style={{ animation: 'fadeInUp 0.25s ease-out' }} />
      <div key={m.id} className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl bg-[#07070d] border border-white/[0.1]"
        style={{ animation: 'modalIn 0.4s cubic-bezier(0.22,1,0.36,1)', boxShadow: `0 0 90px -20px ${hex}66, 0 40px 80px -30px rgba(0,0,0,0.8)` }}>
        <div className="relative h-[360px] overflow-hidden rounded-t-3xl">
          <ExecAvatar member={m} hex={hex} className="w-full h-full" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07070d] via-[#07070d]/50 to-transparent" />
          <div className="pointer-events-none absolute -bottom-20 left-1/4 w-80 h-80 rounded-full blur-[120px] opacity-50" style={{ background: hex }} />
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
            <div className="flex gap-1.5">
              <button onClick={() => go(-1)} data-testid="exec-prev-btn" className="w-9 h-9 rounded-full bg-black/45 backdrop-blur-md border border-white/15 flex items-center justify-center text-white/70 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => go(1)} data-testid="exec-next-btn" className="w-9 h-9 rounded-full bg-black/45 backdrop-blur-md border border-white/15 flex items-center justify-center text-white/70 hover:text-white"><ChevronRight className="w-4 h-4" /></button>
              <span className="self-center ml-1 text-[10px] tracking-[0.2em] text-white/50 tabular-nums">{index + 1} / {execs.length}</span>
            </div>
            <button onClick={onClose} data-testid="close-executive-modal" className="w-9 h-9 rounded-full bg-black/45 backdrop-blur-md border border-white/15 flex items-center justify-center text-white/70 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-7">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-semibold tracking-[0.2em] uppercase border mb-3"
              style={{ background: `${hex}1f`, borderColor: `${hex}55`, color: hex }}>Leadership · {m.roleShort || 'Executive'}</span>
            <h2 className="text-4xl md:text-5xl font-semibold text-white tracking-tight leading-none" data-testid="exec-modal-name">{m.name}</h2>
            <p className="text-sm text-white/60 mt-2">{m.role}</p>
          </div>
        </div>

        <div className="relative px-7 pb-10 space-y-7">
          {m.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">{m.tags.map((t, i) => {
              const th = TAG_HEX[t.color] || '#00E5FF';
              return <span key={i} data-testid={`modal-tag-${i}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-[0.08em] uppercase border" style={{ background: `${th}14`, borderColor: `${th}40`, color: th }}><ShieldCheck className="w-3 h-3" />{t.label}</span>;
            })}</div>
          )}

          <div className="grid grid-cols-3 gap-2">
            <Stat Icon={Calendar} label="Joined" value={m.joinedYear || '—'} hex={hex} />
            <Stat Icon={Target} label="Focus areas" value={m.focusAreas?.length || 0} hex={hex} />
            <Stat Icon={Trophy} label="Notable wins" value={m.achievements?.length || 0} hex={hex} />
          </div>

          {m.contact?.email && (
            <div className="flex gap-2">
              <a href={`mailto:${m.contact.email}`} data-testid="executive-contact-email"
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold text-[#0a0a14] hover:-translate-y-0.5 transition-transform"
                style={{ background: `linear-gradient(90deg, ${hex}, #A78BFA)` }}><Mail className="w-4 h-4" /> Email {m.name.split(' ')[0]}</a>
              <button onClick={copy} data-testid="exec-copy-email" title={m.contact.email}
                className="px-4 rounded-2xl border border-white/[0.1] text-white/60 hover:text-white">{copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}</button>
            </div>
          )}

          {m.quote && (
            <blockquote className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 pl-12">
              <Quote className="absolute left-4 top-5 w-5 h-5" style={{ color: hex }} />
              <p className="text-white/80 italic leading-relaxed">{m.quote}</p>
            </blockquote>
          )}

          {(m.fullBio || m.bio) && <div><Label>About</Label><p className="text-[15px] text-white/70 leading-relaxed whitespace-pre-line">{m.fullBio || m.bio}</p></div>}

          {m.focusAreas?.length > 0 && (
            <div><Label Icon={Target}>Focus areas</Label>
              <div className="flex flex-wrap gap-1.5">{m.focusAreas.map((a, i) => <span key={i} className="px-3 py-1.5 rounded-full text-xs border" style={{ background: `${hex}10`, borderColor: `${hex}30`, color: `${hex}e6` }}>{a}</span>)}</div>
            </div>
          )}

          {m.achievements?.length > 0 && (
            <div><Label Icon={Trophy}>Notable wins</Label>
              <ol className="space-y-2">{m.achievements.map((a, i) => (
                <li key={i} className="flex gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <span className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-[#0a0a14]" style={{ background: hex }}>{i + 1}</span>
                  <span className="text-sm text-white/70 leading-relaxed">{a}</span>
                </li>
              ))}</ol>
            </div>
          )}

          <div className="pt-5 border-t border-white/[0.06] flex items-center justify-between">
            <button onClick={() => go(-1)} className="inline-flex items-center gap-1 text-xs text-white/45 hover:text-white"><ChevronLeft className="w-3.5 h-3.5" />{execs[(index - 1 + execs.length) % execs.length].name}</button>
            <button onClick={() => go(1)} className="inline-flex items-center gap-1 text-xs text-white/45 hover:text-white">{execs[(index + 1) % execs.length].name}<ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};
